// Usados reacondicionados — Taller → Operación → Usados (957).
// Compra con verificación de número de serie (anti-robo) y declaración de
// origen lícito, reacondicionamiento por orden interna (repuestos como
// consumo) y venta por el POS con garantía propia que viaja en el pasaporte.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle, Ban, Banknote, CheckCircle2, ExternalLink, Package, Plus, Recycle, Search, ShieldCheck, Tag, Trash2, UserPlus, Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import {
  Campo, fieldCls, money, useSort, SortTh, tallerRpc, QK, urlPasaporte, fmtFecha, PanelIntegrado, useDialogoEnLinea, TIPOS_EQUIPO,
  type Catalogo, type ConfigTaller,
} from './shared';
import { TurnoInline } from './caja';

// ── Tipos ──────────────────────────────────────────────────────────────────
type EstadoUsado = 'comprado' | 'reacondicionando' | 'a_la_venta' | 'vendido' | 'anulado';
const ESTADOS_USADO: Record<EstadoUsado, { label: string; cls: string }> = {
  comprado:         { label: 'Comprado',         cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' },
  reacondicionando: { label: 'Reacondicionando', cls: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300' },
  a_la_venta:       { label: 'A la venta',       cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  vendido:          { label: 'Vendido',          cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  anulado:          { label: 'Anulado',          cls: 'bg-muted text-muted-foreground line-through' },
};

interface UsadoFila {
  id: string; codigo: string; estado: EstadoUsado; equipo: string; tipo: string; serial: string | null; pasaporte: string;
  vendedor: string; comprador: string | null; fecha_compra: string; precio_compra: number; costo_reacond: number; precio_venta: number | null;
  garantia_dias: number | null; garantia_hasta: string | null; vendido_at: string | null; alertas: string[]; liquidacion_id: string | null;
  liquidacion_estado: string | null; documento_compra: string | null; orden_reacond_id: string | null;
  orden_reacond: { codigo: string; estado: string } | null; margen: number | null;
}
interface VerificacionSerie {
  conocido: boolean;
  equipo: { id: string; tipo: string; marca: string | null; modelo: string | null; modelo_id: string | null; color: string | null; dueno: string; reparaciones: number; pasaporte: string } | null;
  alertas: string[];
}
interface Cliente { id: string; nombre: string; identificacion: string; celular: string | null; email: string | null }
type PagoForm = { forma_pago_id: string; monto: string; referencia: string };

function Kpi({ icon, label, valor, peligro }: { icon: React.ReactNode; label: string; valor: string | number; peligro?: boolean }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon} {label}</div>
        <div className={`mt-1 text-xl font-semibold ${peligro ? 'text-rose-600' : ''}`}>{valor}</div>
      </CardContent>
    </Card>
  );
}

function PagosForm({ formas, pagos, setPagos }: { formas: ConfigTaller['formas']; pagos: PagoForm[]; setPagos: (p: PagoForm[]) => void }) {
  return (
    <div className="space-y-2">
      {pagos.map((p, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2">
          <select className={`${fieldCls} w-56`} value={p.forma_pago_id} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, forma_pago_id: e.target.value } : x)))}>
            <option value="">— Forma de pago —</option>
            {formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
          </select>
          <input type="number" step="0.01" min={0} className={`${fieldCls} w-28 text-right`} placeholder="Monto" value={p.monto} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x)))} />
          <input className={`${fieldCls} w-44`} placeholder="Referencia / voucher" value={p.referencia} onChange={(e) => setPagos(pagos.map((x, j) => (j === i ? { ...x, referencia: e.target.value } : x)))} />
          {pagos.length > 1 && <button type="button" aria-label="Quitar" onClick={() => setPagos(pagos.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-muted-foreground" /></button>}
        </div>
      ))}
      <Button size="sm" variant="ghost" onClick={() => setPagos([...pagos, { forma_pago_id: '', monto: '', referencia: '' }])}><Plus className="mr-1 h-4 w-4" /> Otro pago (pago mixto)</Button>
    </div>
  );
}

// ── Comprar usado (asistente de 2 pasos) ────────────────────────────────────
function ComprarUsado({ cat, onClose }: { cat?: Catalogo; onClose: () => void }) {
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const [paso, setPaso] = useState<1 | 2>(1);
  const [serial, setSerial] = useState('');
  const [ident, setIdent] = useState('');
  const [ver, setVer] = useState<VerificacionSerie | null>(null);

  const [vend, setVend] = useState({ nombre: '', celular: '', email: '' });
  const [eq, setEq] = useState({ tipo: 'consola', modelo_id: '', marca: '', modelo: '', color: '' });
  const [precioCompra, setPrecioCompra] = useState('');
  const [precioVenta, setPrecioVenta] = useState('');
  const [declaracion, setDeclaracion] = useState(false);
  const [confirmarAlertas, setConfirmarAlertas] = useState(false);
  const [emitirLiquidacion, setEmitirLiquidacion] = useState(true);
  const [documentoCompra, setDocumentoCompra] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const verificar = useMutation({
    mutationFn: () => tallerRpc<VerificacionSerie>('usado_verificar', { _serial: serial.trim(), _vendedor_ident: ident.trim() || null }),
    onSuccess: (r) => {
      setVer(r);
      if (r.conocido && r.equipo) setEq({ tipo: r.equipo.tipo, modelo_id: r.equipo.modelo_id ?? '', marca: r.equipo.marca ?? '', modelo: r.equipo.modelo ?? '', color: r.equipo.color ?? '' });
      setPaso(2);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const comprar = useMutation({
    mutationFn: () => tallerRpc<{ id: string; codigo: string; liquidacion_id: string | null; alertas: string[] }>('usado_comprar', {
      _d: {
        vendedor: { identificacion: ident.trim(), nombre: vend.nombre.trim(), celular: vend.celular.trim() || null, email: vend.email.trim() || null },
        equipo: { tipo: eq.tipo, modelo_id: eq.modelo_id || null, marca: eq.marca.trim() || null, modelo: eq.modelo.trim() || null, serial: serial.trim(), color: eq.color.trim() || null },
        precio_compra: Number(precioCompra) || 0,
        precio_venta: precioVenta ? Number(precioVenta) : null,
        declaracion_origen: declaracion,
        confirmar_alertas: confirmarAlertas,
        liquidacion: emitirLiquidacion,
        documento_compra: emitirLiquidacion ? null : (documentoCompra.trim() || null),
        observaciones: observaciones.trim() || null,
      },
    }),
    onSuccess: (r) => {
      toast.success(`Usado ${r.codigo} registrado`);
      if (r.liquidacion_id) toast.info('La liquidación de compra quedó POR EMITIR en Financiero → Liquidación de compra', {
        action: { label: 'Ir', onClick: () => openTab({ id: '/cxp/liquidacion-compra', title: 'Liquidación de compra', path: '/cxp/liquidacion-compra' }) },
      });
      void qc.invalidateQueries({ queryKey: [QK] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const modelos = (cat?.modelos ?? []).filter((m) => m.activo && m.tipo === eq.tipo);
  const hayAlertas = (ver?.alertas.length ?? 0) > 0;
  const listo = declaracion && (!hayAlertas || confirmarAlertas) && Number(precioCompra) > 0 && vend.nombre.trim().length >= 3 && ident.trim().length >= 6
    && (emitirLiquidacion || documentoCompra.trim().length > 0);

  return (
    <PanelIntegrado title="Comprar usado" onClose={onClose} wide>
      {paso === 1 && (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">Primero verifique el número de serie: si el equipo ya tiene historial en el taller se muestra aquí (anti-robo).</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Campo label="Número de serie *"><input className={`${fieldCls} font-mono uppercase`} autoFocus value={serial} onChange={(e) => setSerial(e.target.value)} /></Campo>
            <Campo label="Identificación del vendedor *"><input className={fieldCls} value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="Cédula / RUC" /></Campo>
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={serial.trim().length < 4 || ident.trim().length < 6} isLoading={verificar.isPending} onClick={() => verificar.mutate()}>
              <Search className="mr-1 h-4 w-4" /> Verificar
            </Button>
          </div>
        </div>
      )}

      {paso === 2 && (
        <div className="space-y-3 text-sm">
          {hayAlertas && (
            <div className="space-y-1 rounded-md border border-rose-300 bg-rose-50 p-3 text-rose-900 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-200">
              <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-4 w-4" /> Alertas del número de serie</div>
              <ul className="list-disc pl-5">{ver!.alertas.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </div>
          )}
          {ver?.conocido && ver.equipo && (
            <div className="space-y-1 rounded-md border bg-muted/30 p-3 text-xs">
              <div className="font-medium">Equipo conocido: {[ver.equipo.marca, ver.equipo.modelo].filter(Boolean).join(' ')} · dueño actual {ver.equipo.dueno}</div>
              <div>{ver.equipo.reparaciones} reparación(es) entregada(s) en este taller.</div>
              <a href={urlPasaporte(ver.equipo.pasaporte)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">Ver pasaporte <ExternalLink className="h-3 w-3" /></a>
            </div>
          )}

          <div className="font-semibold">Vendedor</div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Campo label="Identificación *"><input className={fieldCls} value={ident} disabled readOnly /></Campo>
            <Campo label="Nombre *"><input className={fieldCls} value={vend.nombre} onChange={(e) => setVend({ ...vend, nombre: e.target.value })} /></Campo>
            <Campo label="Celular (WhatsApp)"><input className={fieldCls} value={vend.celular} onChange={(e) => setVend({ ...vend, celular: e.target.value })} placeholder="09…" /></Campo>
            <Campo label="Correo"><input type="email" className={fieldCls} value={vend.email} onChange={(e) => setVend({ ...vend, email: e.target.value })} /></Campo>
          </div>

          <div className="font-semibold">Equipo</div>
          {ver?.conocido ? (
            <div className="text-xs text-muted-foreground">Serie {serial.trim().toUpperCase()} · {[eq.marca, eq.modelo].filter(Boolean).join(' ')}{eq.color ? ` · ${eq.color}` : ''}</div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              <Campo label="Tipo">
                <select className={fieldCls} value={eq.tipo} onChange={(e) => setEq({ ...eq, tipo: e.target.value, modelo_id: '' })}>
                  {TIPOS_EQUIPO.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Campo>
              <Campo label="Modelo del catálogo">
                <select className={fieldCls} value={eq.modelo_id} onChange={(e) => setEq({ ...eq, modelo_id: e.target.value })}>
                  <option value="">— Otro (escribir) —</option>
                  {modelos.map((m) => <option key={m.id} value={m.id}>{m.marca} {m.modelo}{m.revision ? ` ${m.revision}` : ''}</option>)}
                </select>
              </Campo>
              {!eq.modelo_id && <>
                <Campo label="Marca"><input className={fieldCls} value={eq.marca} onChange={(e) => setEq({ ...eq, marca: e.target.value })} /></Campo>
                <Campo label="Modelo"><input className={fieldCls} value={eq.modelo} onChange={(e) => setEq({ ...eq, modelo: e.target.value })} /></Campo>
              </>}
              <Campo label="Color"><input className={fieldCls} value={eq.color} onChange={(e) => setEq({ ...eq, color: e.target.value })} /></Campo>
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <Campo label="Precio de compra ($) *"><input type="number" min={0} step="0.01" className={fieldCls} value={precioCompra} onChange={(e) => setPrecioCompra(e.target.value)} /></Campo>
            <Campo label="Precio de venta sugerido (PVP con IVA, opcional)"><input type="number" min={0} step="0.01" className={fieldCls} value={precioVenta} onChange={(e) => setPrecioVenta(e.target.value)} /></Campo>
          </div>

          <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" checked={declaracion} onChange={(e) => setDeclaracion(e.target.checked)} />
            <span>El vendedor firmó la declaración de origen lícito (copia de cédula adjunta al expediente) *</span></label>
          {hayAlertas && (
            <label className="flex items-start gap-2 text-rose-800 dark:text-rose-200"><input type="checkbox" className="mt-0.5" checked={confirmarAlertas} onChange={(e) => setConfirmarAlertas(e.target.checked)} />
              <span>Revisé las alertas y continúo bajo mi responsabilidad *</span></label>
          )}

          <label className="flex items-center gap-2"><input type="checkbox" checked={emitirLiquidacion} onChange={(e) => setEmitirLiquidacion(e.target.checked)} /> Emitir liquidación de compra (recomendado)</label>
          {!emitirLiquidacion && (
            <Campo label="N.º de factura o nota de venta del vendedor *"><input className={fieldCls} value={documentoCompra} onChange={(e) => setDocumentoCompra(e.target.value)} /></Campo>
          )}

          <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Campo>

          <div className="flex justify-between">
            <Button size="sm" variant="outline" onClick={() => setPaso(1)}>Volver</Button>
            <Button size="sm" disabled={!listo} isLoading={comprar.isPending} onClick={() => comprar.mutate()}><Package className="mr-1 h-4 w-4" /> Registrar compra</Button>
          </div>
        </div>
      )}
    </PanelIntegrado>
  );
}

// ── Vender usado ─────────────────────────────────────────────────────────
function VenderUsado({ u, cfg, onClose }: { u: UsadoFila; cfg?: ConfigTaller; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [cliente, setCliente] = useState<Cliente | null>(null);
  const [nuevoCli, setNuevoCli] = useState<{ nombre: string; identificacion: string; celular: string; email: string } | null>(null);
  const { data: clientes = [] } = useQuery<Cliente[]>({ queryKey: [QK, 'clientes', q], queryFn: () => tallerRpc('clientes_buscar', { _q: q }), enabled: !cliente && q.trim().length >= 2 });
  const formas = (cfg?.formas ?? []).filter((f) => f.id !== cfg?.config?.forma_anticipo_id && f.tipo !== 'credito');
  const total = Number(u.precio_venta ?? 0);
  const [pagos, setPagos] = useState<PagoForm[]>([{ forma_pago_id: formas[0]?.id ?? '', monto: total ? String(total) : '', referencia: '' }]);

  const crearCliente = useMutation({
    mutationFn: () => tallerRpc<{ id: string }>('cliente_crear', { _d: nuevoCli }),
    onSuccess: (r) => { setCliente({ id: r.id, nombre: nuevoCli!.nombre, identificacion: nuevoCli!.identificacion, celular: nuevoCli!.celular || null, email: nuevoCli!.email || null }); setNuevoCli(null); toast.success('Cliente registrado'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const limpiar = (p: PagoForm[]) => p.filter((x) => x.forma_pago_id && Number(x.monto) > 0).map((x) => ({ forma_pago_id: x.forma_pago_id, monto: Number(x.monto), referencia: x.referencia || null }));
  const suma = limpiar(pagos).reduce((a, x) => a + x.monto, 0);
  const vender = useMutation({
    mutationFn: () => tallerRpc<{ garantia_hasta: string; pasaporte: string }>('usado_vender', { _usado: u.id, _cliente: cliente!.id, _pagos: limpiar(pagos) }),
    onSuccess: (r) => {
      toast.success(`Vendido · garantía hasta ${fmtFecha(r.garantia_hasta)}`, {
        action: { label: 'Ver pasaporte', onClick: () => window.open(urlPasaporte(r.pasaporte), '_blank') },
      });
      void qc.invalidateQueries({ queryKey: [QK] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <PanelIntegrado title={`Vender ${u.codigo} · ${u.equipo}`} onClose={onClose} wide>
      <TurnoInline motivo="Para vender el usado se necesita el turno de la caja del taller abierto." />
      <div className="space-y-3 text-sm">
        <div className="text-xs text-muted-foreground">Precio de venta: <strong>{money(total)}</strong> (IVA incluido)</div>

        <div className="font-semibold">Comprador</div>
        {cliente ? (
          <div className="flex items-start justify-between rounded-md border bg-muted/30 p-2">
            <div><div className="font-medium">{cliente.nombre}</div><div className="text-xs text-muted-foreground">{cliente.identificacion} · {cliente.celular ?? 'sin celular'}</div></div>
            <button type="button" aria-label="Cambiar" onClick={() => setCliente(null)}>✕</button>
          </div>
        ) : nuevoCli ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <Campo label="Nombres y apellidos"><input className={fieldCls} value={nuevoCli.nombre} onChange={(e) => setNuevoCli({ ...nuevoCli, nombre: e.target.value })} /></Campo>
            <Campo label="Cédula / RUC"><input className={fieldCls} value={nuevoCli.identificacion} onChange={(e) => setNuevoCli({ ...nuevoCli, identificacion: e.target.value })} /></Campo>
            <Campo label="Celular"><input className={fieldCls} value={nuevoCli.celular} onChange={(e) => setNuevoCli({ ...nuevoCli, celular: e.target.value })} /></Campo>
            <Campo label="Correo"><input type="email" className={fieldCls} value={nuevoCli.email} onChange={(e) => setNuevoCli({ ...nuevoCli, email: e.target.value })} /></Campo>
            <div className="flex gap-2 sm:col-span-2">
              <Button size="sm" isLoading={crearCliente.isPending} onClick={() => crearCliente.mutate()}>Registrar cliente</Button>
              <Button size="sm" variant="ghost" onClick={() => setNuevoCli(null)}>Cancelar</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <input className={`${fieldCls} pl-7`} autoFocus placeholder="Nombre, cédula o celular" value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
              <Button size="sm" variant="outline" onClick={() => setNuevoCli({ nombre: /\d/.test(q) ? '' : q, identificacion: /^\d+$/.test(q) ? q : '', celular: '', email: '' })}><UserPlus className="mr-1 h-4 w-4" /> Nuevo</Button>
            </div>
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {clientes.map((c) => (
                <button key={c.id} type="button" onClick={() => setCliente(c)} className="block w-full rounded border px-2 py-1 text-left hover:bg-muted">
                  <span className="font-medium">{c.nombre}</span> <span className="text-xs text-muted-foreground">{c.identificacion} · {c.celular ?? ''}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {cliente && (
          <>
            <div className="font-semibold">Pagos (total {money(total)})</div>
            <PagosForm formas={formas} pagos={pagos} setPagos={setPagos} />
            {suma + 0.004 < total && <div className="text-xs text-red-600">Faltan {money(total - suma)} para cubrir el precio.</div>}
            <div className="flex justify-end">
              <Button size="sm" disabled={suma + 0.004 < total} isLoading={vender.isPending} onClick={() => vender.mutate()}><Banknote className="mr-1 h-4 w-4" /> Vender y facturar</Button>
            </div>
          </>
        )}
      </div>
    </PanelIntegrado>
  );
}

// ── Pantalla principal ───────────────────────────────────────────────────
export function UsadosTaller() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const openTab = useTabsStore((s) => s.openTab);
  const [vista, setVista] = useState<'lista' | 'comprar'>('lista');
  const [vendiendo, setVendiendo] = useState<UsadoFila | null>(null);

  const { data = [], isLoading } = useQuery<UsadoFila[]>({ queryKey: [QK, 'usados'], queryFn: () => tallerRpc('usados_listar') });
  const { data: cat } = useQuery<Catalogo>({ queryKey: [QK, 'catalogo'], queryFn: () => tallerRpc('catalogo') });
  const { data: cfg } = useQuery<ConfigTaller>({ queryKey: [QK, 'config'], queryFn: () => tallerRpc('config_get') });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });

  const publicar = useMutation({
    mutationFn: (x: { usado: string; precio: number; garantia: number }) => tallerRpc('usado_publicar', { _usado: x.usado, _precio_venta: x.precio, _garantia_dias: x.garantia }),
    onSuccess: () => { toast.success('Publicado a la venta'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const reacondicionar = useMutation({
    mutationFn: (usado: string) => tallerRpc<{ orden_id: string; codigo: string }>('usado_reacondicionar', { _usado: usado }),
    onSuccess: (r) => {
      toast.success(`Orden interna ${r.codigo} creada: los repuestos salen como consumo`);
      inv();
      try { sessionStorage.setItem('taller_abrir_orden', r.orden_id); } catch { /* sin almacenamiento */ }
      openTab({ id: '/taller/ordenes', title: 'Órdenes', path: '/taller/ordenes' });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (x: { usado: string; motivo: string }) => tallerRpc('usado_anular', { _usado: x.usado, _motivo: x.motivo }),
    onSuccess: () => { toast.success('Usado anulado'); inv(); }, onError: (e) => toast.error(getErrorMessage(e)),
  });

  const sort = useSort<UsadoFila>(data, (r, k) => (k === 'codigo' ? r.codigo : k === 'estado' ? r.estado : k === 'equipo' ? r.equipo
    : k === 'vendedor' ? r.vendedor : k === 'comprador' ? r.comprador : k === 'fecha' ? r.fecha_compra
    : k === 'compra' ? Number(r.precio_compra) : k === 'venta' ? Number(r.precio_venta ?? 0) : k === 'margen' ? Number(r.margen ?? 0) : null));

  const kpis = useMemo(() => ({
    stock: data.filter((u) => ['comprado', 'reacondicionando', 'a_la_venta'].includes(u.estado)).length,
    venta: data.filter((u) => u.estado === 'a_la_venta').length,
    vendidos: data.filter((u) => u.estado === 'vendido').length,
    margen: data.filter((u) => u.estado === 'vendido').reduce((a, u) => a + Number(u.margen ?? 0), 0),
  }), [data]);

  const publicarDlg = async (u: UsadoFila) => {
    const v = await dlg.pedir(`Publicar ${u.codigo}`, [
      { key: 'precio', label: 'Precio de venta (PVP con IVA)', tipo: 'number', valor: u.precio_venta != null ? String(u.precio_venta) : '', minimo: 1 },
      { key: 'garantia', label: 'Días de garantía', tipo: 'number', valor: String(u.garantia_dias ?? ''), minimo: 1 },
    ], { aceptar: 'Publicar' });
    if (v) publicar.mutate({ usado: u.id, precio: Number(v.precio), garantia: Number(v.garantia) });
  };
  const anularDlg = async (u: UsadoFila) => {
    const v = await dlg.pedir(`Anular ${u.codigo}`, [{ key: 'motivo', label: 'Motivo', minimo: 5, multilinea: true }], { aceptar: 'Anular' });
    if (v) anular.mutate({ usado: u.id, motivo: v.motivo });
  };

  return (
    <div className="space-y-3 p-4 sm:p-6">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-xl font-semibold"><Recycle className="h-5 w-5" /> Usados reacondicionados</h1>
        {vista === 'lista' && <Button size="sm" onClick={() => setVista('comprar')}><Plus className="mr-1 h-4 w-4" /> Comprar usado</Button>}
      </div>

      {vista === 'comprar' && <ComprarUsado cat={cat} onClose={() => setVista('lista')} />}
      {vendiendo && <VenderUsado u={vendiendo} cfg={cfg} onClose={() => setVendiendo(null)} />}

      {vista === 'lista' && !vendiendo && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi icon={<Package className="h-4 w-4" />} label="En stock" valor={kpis.stock} />
            <Kpi icon={<Tag className="h-4 w-4" />} label="A la venta" valor={kpis.venta} />
            <Kpi icon={<CheckCircle2 className="h-4 w-4" />} label="Vendidos" valor={kpis.vendidos} />
            <Kpi icon={<Banknote className="h-4 w-4" />} label="Margen total" valor={money(kpis.margen)} peligro={kpis.margen < 0} />
          </div>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pl-3 pr-2">N°</th>
                  <SortTh col="codigo" sort={sort}>Código</SortTh>
                  <SortTh col="estado" sort={sort}>Estado</SortTh>
                  <SortTh col="equipo" sort={sort}>Equipo</SortTh>
                  <SortTh col="vendedor" sort={sort}>Vendedor</SortTh>
                  <SortTh col="comprador" sort={sort}>Comprador</SortTh>
                  <SortTh col="fecha" sort={sort}>Compra</SortTh>
                  <SortTh col="compra" sort={sort} className="text-right">Precio compra</SortTh>
                  <th className="py-2 pr-2 text-right">Reacond.</th>
                  <SortTh col="venta" sort={sort} className="text-right">Precio venta</SortTh>
                  <SortTh col="margen" sort={sort} className="text-right">Margen</SortTh>
                  <th className="py-2 pr-2">Garantía</th>
                  <th className="py-2 pr-3">Acciones</th>
                </tr></thead>
                <tbody>
                  {isLoading && <tr><td colSpan={13} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
                  {!isLoading && sort.sorted.length === 0 && <tr><td colSpan={13} className="py-8 text-center text-muted-foreground">Sin usados registrados.</td></tr>}
                  {sort.sorted.map((u, i) => (
                    <tr key={u.id} className="border-b last:border-0 align-top hover:bg-muted/40">
                      <td className="py-2 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-2 font-mono font-semibold">
                        {u.codigo}
                        {u.alertas.length > 0 && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-rose-500" aria-label="Con alertas" />}
                        {u.liquidacion_id && u.liquidacion_estado && u.liquidacion_estado !== 'autorizado' && (
                          <div className="text-[10px] font-normal text-amber-700">liquidación {u.liquidacion_estado}</div>
                        )}
                        {u.documento_compra && <div className="text-[10px] font-normal text-muted-foreground">doc. {u.documento_compra}</div>}
                      </td>
                      <td className="py-2 pr-2"><span className={`rounded-full px-2 py-0.5 text-xs ${ESTADOS_USADO[u.estado].cls}`}>{ESTADOS_USADO[u.estado].label}</span>
                        {u.orden_reacond && <div className="text-[11px] text-muted-foreground">{u.orden_reacond.codigo} · {u.orden_reacond.estado}</div>}</td>
                      <td className="py-2 pr-2">{u.equipo}{u.serial && <div className="font-mono text-[11px] text-muted-foreground">{u.serial}</div>}
                        <a href={urlPasaporte(u.pasaporte)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[11px] text-primary underline">Pasaporte <ExternalLink className="h-3 w-3" /></a></td>
                      <td className="py-2 pr-2">{u.vendedor}</td>
                      <td className="py-2 pr-2">{u.comprador ?? '—'}</td>
                      <td className="py-2 pr-2">{fmtFecha(u.fecha_compra)}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{money(Number(u.precio_compra))}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{Number(u.costo_reacond) ? money(Number(u.costo_reacond)) : '—'}</td>
                      <td className="py-2 pr-2 text-right tabular-nums">{u.precio_venta != null ? money(Number(u.precio_venta)) : '—'}</td>
                      <td className={`py-2 pr-2 text-right tabular-nums ${u.margen != null && u.margen < 0 ? 'text-rose-600' : ''}`}>{u.margen != null ? money(Number(u.margen)) : '—'}</td>
                      <td className="py-2 pr-2 text-xs">{u.garantia_hasta ? fmtFecha(u.garantia_hasta) : '—'}</td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-wrap gap-1">
                          {['comprado', 'a_la_venta'].includes(u.estado) && (
                            <Button size="sm" variant="outline" isLoading={reacondicionar.isPending} onClick={() => reacondicionar.mutate(u.id)}><Wrench className="mr-1 h-3.5 w-3.5" /> Reacondicionar</Button>
                          )}
                          {['comprado', 'a_la_venta'].includes(u.estado) && (
                            <Button size="sm" variant="outline" onClick={() => void publicarDlg(u)}><Tag className="mr-1 h-3.5 w-3.5" /> Publicar / precio</Button>
                          )}
                          {u.estado === 'a_la_venta' && (
                            <Button size="sm" onClick={() => setVendiendo(u)}><ShieldCheck className="mr-1 h-3.5 w-3.5" /> Vender</Button>
                          )}
                          {['comprado', 'a_la_venta'].includes(u.estado) && (
                            <Button size="sm" variant="ghost" onClick={() => void anularDlg(u)}><Ban className="mr-1 h-3.5 w-3.5" /> Anular</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {data.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold">
                  <td colSpan={10} className="py-2 pl-3">Total: {data.length} usado(s)</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{money(kpis.margen)}</td>
                  <td colSpan={2} />
                </tr></tfoot>}
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
